<script setup>
/**
 * GiftSend: the gift send modal (2026-10-22, Chris; `Claude outputs/
 * gift-flow-review-2026-10-22/design/DESIGN.md` §1 to §3 and "The pattern:
 * say it once", mockups S1 to S6). Supersedes the 2026-10-21 screen's
 * "To · from · Change" form, "Or let us send it", the under-button line, the
 * pane line and the fine print.
 *
 * One composer, stacked from the top, nothing pinned to the foot:
 *  - the header: "A gift for / <Name>" (the dotted name renames the gift
 *    before anything goes out), ⓘ ("How sending works", HelpPopover) and ✕;
 *  - To (ToBox): the circle, an email or a phone, one token;
 *  - the note: a real field on the paper card under a gold seal, its eyebrow
 *    "<SIGN AS ⌄> was thinking of you" (the sign-as menu lives in the name);
 *  - the primary button, then the secondary (once someone is named) and the
 *    lock pill "🔒 Only <Name> can open it" on one row.
 * On a computer (900px+) the left pane is the letter as they will open it
 * (GiftPreview), read-only, mirroring the note as it is typed; on a phone the
 * note card sits on the picture and "What <Name> opens · Read it as they will ›"
 * opens the preview as a sheet inside the modal.
 *
 * The primary, per state (DESIGN §3 table; `primaryFor`): nobody named, a
 * phone "Share with <Name>" (the share sheet inside the tap), a computer
 * "Copy <Name>'s link" → "Copied" + "Now paste it wherever you talk to
 * <Name>."; a member "Send to <Name>", an email "Email it to <Name>" → "Sent",
 * then it closes. Once someone is named the link steps aside: "Copy link
 * instead" (computer) / "Share" (phone). One invite throughout (R4.33).
 *
 * A typed phone number (texting, 2026-10-31: we never text a number someone
 * else typed): the sender sends the /g/ link. On a phone "Text it to <Name>"
 * opens their own Messages to that number with the words and the link; on a
 * computer "Copy <Name>'s link" → "Now paste it into a text to <Name>." The
 * number never goes to the server.
 *
 * Instructions show inline only on the first two opens in this browser
 * (`hintsDue`); after that they live behind ⓘ. Feedback always shows.
 *
 * Kept: the kindness check on an edited note (R4.46, committed to the prepared
 * link 600 ms after the last keystroke so a share stays inside the tap), the
 * rating first (R7.9), the password moment (R7.11), the opted-out address
 * (R10.8), the draft that survives a stray close, the link to copy by hand
 * when sharing and copying both fail (R4.53).
 */
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { store } from '@/store'
import { profileFieldRejection } from '@/utils/profile-safety'
import { careDraft, careSend, careLink, carePrepareLink, careRename, careContactCheck, carePreview, getMyFriends } from '@/api-service'
import { copyText, prefersShareSheet, handOver, giftShareText } from '@/utils/care-link'
import { giftShareMode, sendErrorReason, sendErrorMessage, sendShortOf } from '@/utils/care-package'
import {
  possessive, primaryFor, secondaryFor, lockLine, copiedLine, HELP_LINES, TO_HINT, hintsDue, markHintSeen,
  contactKind, isPhoneish, phoneDigits,
} from '@/utils/gift-send'
import { saveDraft, loadDraft, clearDraft } from '@/utils/care-draft'
import { track, trackShare } from '@/utils/analytics'
import { passwordStep } from '@/utils/stage-four'
import SendPassword from '@/components/ui/SendPassword.vue'
import GiftPreview from '@/components/ui/share/GiftPreview.vue'
import ToBox from '@/components/ui/share/ToBox.vue'
import HelpPopover from '@/components/ui/share/HelpPopover.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  // {is_gift, for_name, for_querent, signed_as, from_name, gift_note, reading_session_id, from_choices,
  //  password_moment, sent, invite, opened}
  gift: { type: Object, required: true },
  sessionId: { type: String, default: '' },
  // The reading's picture: the top of the letter. None: the note alone.
  image: { type: String, default: '' },
  // R7.9: her reading for someone is rated before it goes.
})
const emit = defineEmits(['close', 'sent', 'linked', 'changed', 'need-carats'])

const g = ref({ ...props.gift })
const sid = computed(() => props.sessionId || store.session_id)
const rid = computed(() => (g.value && g.value.reading_session_id) || store.latest_reading_session)

const cpSigned = ref(g.value.signed_as || 'nickname')
const cpNote = ref(g.value.gift_note || '')
const name = computed(() => (g.value.for_name || '').trim() || 'them')

// ── What has gone out: the note is as sent, nothing is renamed ────────────
const alreadySent = !!g.value.sent
const opened = computed(() => !!g.value.opened)
const handedOver = ref(false)
const wentOut = computed(() => alreadySent || handedOver.value)

// ── First-time hints: inline on the first two opens, then only behind ⓘ ───
const hints = hintsDue()

// ── To: one token, a member, an email or a phone ──────────────────────────
// A gift already bound to a member opens with that member in the box.
function memberToken () {
  const m = giftShareMode(g.value)
  if (!m || m.contact) return null
  const label = (g.value.for_member_name || g.value.for_querent_name || g.value.for_name || '').trim() || 'Them'
  return { kind: 'member', label, uuid: g.value.for_querent_uuid || '', value: '' }
}
const toToken = ref(memberToken())
const toText = ref('')
const toBoxEl = ref(null)

// What they typed last time survives a stray click, a closed tab, a reload.
const cpDraft = alreadySent ? null : loadDraft(rid.value)
if (cpDraft) {
  if (typeof cpDraft.note === 'string' && cpDraft.note) cpNote.value = cpDraft.note
  if (cpDraft.token && cpDraft.token.kind && cpDraft.token.kind !== 'member' && !toToken.value) toToken.value = cpDraft.token
  else if (typeof cpDraft.contact === 'string') toText.value = cpDraft.contact
}
watch([cpNote, toText, toToken], ([note, contact, token]) => {
  if (!wentOut.value) saveDraft(rid.value, { note, contact, token: token && token.kind !== 'member' ? token : null })
})

// The kind of contact in the box: the token, else what is typed (a whole
// address, or digits, already turn the button).
const toKind = computed(() => {
  if (toToken.value) return toToken.value.kind
  const k = contactKind(toText.value)
  if (k) return k
  return isPhoneish(toText.value) ? 'phone' : ''
})
const contactValue = computed(() => {
  if (toToken.value) return toToken.value.kind === 'member' ? '' : toToken.value.value
  const k = contactKind(toText.value)
  if (k === 'email') return toText.value.trim()
  if (k === 'phone') return phoneDigits(toText.value)
  return ''
})
// The To box stays while something can still go somewhere else: not once
// they opened it, and not for a member gift already sent.
const member = computed(() => toKind.value === 'member')
const showTo = computed(() => !opened.value && !(alreadySent && giftShareMode(g.value) && !giftShareMode(g.value).contact))

// '', 'drafting', 'sending', then a send that did not go:
// 'self' | 'sms_unavailable' | 'invalid_contact' | 'delivery_failed' | 'opted_out' | 'already_sent' | 'error'.
const cpStatus = ref('')
const cpError = ref('')
const drafting = computed(() => cpStatus.value === 'drafting')
const contactHint = ref('')

// ── The lane and its buttons ──────────────────────────────────────────────
const canShareSheet = prefersShareSheet()
const lane = computed(() => toKind.value || (canShareSheet ? 'share' : 'copy'))
// What the primary just did: '' | 'sent' | 'copied'. What the secondary did: '' | 'copied'.
const done = ref('')
const secDone = ref('')
const primary = computed(() => primaryFor(lane.value, name.value, { done: done.value, phone: canShareSheet }))
const secondary = computed(() => secondaryFor(lane.value, { phone: canShareSheet, done: secDone.value }))
const copiedSaid = computed(() => done.value === 'copied' || secDone.value === 'copied')
watch(lane, () => { if (done.value !== 'sent') done.value = ''; cpError.value = ''; contactHint.value = '' })
let closeTimer = null
function closeSoon () {
  clearTimeout(closeTimer)
  closeTimer = setTimeout(() => emit('close'), 1100)
}

// ── The layout: a computer's two panes, a phone's one ─────────────────────
const WIDE = '(min-width: 900px)'
const wide = ref(false)
let wideQuery = null
function onWide () { wide.value = !!(wideQuery && wideQuery.matches) }
const helpLines = computed(() => HELP_LINES(name.value, { wide: wide.value }))

// ── Their version and the Mystic's first message, written now (R4.6) ──────
const preview = ref(null)
const previewOpen = ref(false)
let previewTracked = false
async function loadPreview () {
  try {
    const r = await carePreview(sid.value, rid.value)
    if (r && r.letter) preview.value = r
    // Reopened after it went out, the note as sent is the server's (the
    // page's copy can be the send's answer without it: the note read empty).
    if (r && wentOut.value && !cpNote.value && r.gift_note) { cpNote.value = r.gift_note; cpNoteCommitted.value = r.gift_note.trim() }
  } catch (e) { /* the peek says the Mystic is writing it; the send writes it anyway */ }
}
const peekCards = computed(() => ((preview.value && preview.value.letter && preview.value.letter.cards) || []).slice(0, 4))
const peekTitle = computed(() => (preview.value && preview.value.letter && preview.value.letter.summary_title) || '')
function openPreview () {
  previewOpen.value = true
  if (!previewTracked && !wentOut.value) { previewTracked = true; track('gift_preview_open', { lane: lane.value }) }
}

// ── Names: the gift's (the header) and the sender's (the eyebrow) ─────────
const cpSaving = ref(false)
const cpFrom = ref(g.value.from_name || store.querent_name || '')
const seekerNameAtOpen = (!g.value.signed_name && g.value.from_name) || ''
const seekerName = computed(() => store.querent_name || seekerNameAtOpen)
const fromName = computed(() => (cpFrom.value || '').trim() || seekerName.value || 'Someone')
async function cpRename (fields) {
  if (cpSaving.value) return null
  cpSaving.value = true
  try {
    const r = await careRename(sid.value, rid.value, fields)
    if (r) {
      // Picking a member sets where it goes, never what they are called.
      const keep = fields.for_name === undefined ? { for_name: g.value.for_name } : {}
      g.value = { ...g.value, ...r, ...keep }
      cpSigned.value = r.signed_as || cpSigned.value
      if (r.from_name) cpFrom.value = r.from_name
      if (r.gift_note !== undefined && fields.gift_note === undefined) cpNote.value = r.gift_note || cpNote.value
      emit('changed', { ...r, ...keep })
      // A new name: their version and the share sheet's words follow it.
      if (fields.for_name !== undefined || fields.from_name !== undefined) { loadPreview(); cpPrepare() }
    }
    return r
  } catch (e) { return null } finally { cpSaving.value = false }
}

// The gift's name, renamed in the header ("A gift for Mom & Dad"), only before anything goes out.
const renaming = ref(false)
const renameText = ref('')
const renameInput = ref(null)
function startRename () {
  if (wentOut.value) return
  renameText.value = g.value.for_name || ''
  renaming.value = true
  nextTick(() => { try { renameInput.value && (renameInput.value.focus(), renameInput.value.select()) } catch (e) { /* */ } })
}
function commitRename () {
  if (!renaming.value) return
  renaming.value = false
  const n = renameText.value.trim()
  if (n && n !== g.value.for_name) cpRename({ for_name: n })
}

// Sign as, in the eyebrow (S6): the choices, the current one ticked, "Sign another way…".
const signOpen = ref(false)
const signTyping = ref(false)
const signText = ref('')
const signInput = ref(null)
const signRoot = ref(null)
const signChoices = computed(() => {
  const all = (g.value.from_choices || []).filter(Boolean)
  return all.includes(fromName.value) ? all : [fromName.value, ...all]
})
function toggleSign () {
  if (wentOut.value) return
  signOpen.value = !signOpen.value
  signTyping.value = false
}
function pickSign (n) {
  signOpen.value = false
  if (n !== g.value.from_name) { cpFrom.value = n; cpRename({ from_name: n }) }
}
function signAnotherWay () {
  signTyping.value = true
  signText.value = ''
  nextTick(() => { try { signInput.value && signInput.value.focus() } catch (e) { /* */ } })
}
function commitSign () {
  const n = signText.value.trim()
  signOpen.value = false
  signTyping.value = false
  if (n) pickSign(n)
}
function onDocDown (e) {
  if (signOpen.value && signRoot.value && !signRoot.value.contains(e.target)) { signOpen.value = false; signTyping.value = false }
}

// ── The circle (my-friends), fetched when the box is first used ───────────
const friends = ref(null)
async function loadFriends () {
  if (friends.value !== null) return
  friends.value = []
  try { const r = await getMyFriends(sid.value); friends.value = (r && r.friends) || [] } catch (e) { /* typed contacts still work */ }
}
// Members can be picked only before anything has gone out (the server refuses after).
const circle = computed(() => (wentOut.value ? [] : (friends.value || [])))
async function onPicked (token) {
  cpError.value = ''
  if (token.kind === 'member') {
    // Where it goes, not what they are called: the letter still says the gift's name.
    let r = await cpRename({ for_querent_uuid: token.uuid })
    if (!r) r = await cpRename({ for_querent_uuid: token.uuid, for_name: g.value.for_name })   // an older server wants the name too
    if (!r) { toToken.value = null; cpError.value = sendErrorMessage(null) }
  }
}
async function onCleared () {
  if (giftShareMode(g.value) && !giftShareMode(g.value).contact && !wentOut.value) {
    await cpRename({ for_querent_uuid: '' })
  }
}

// ── The note: their words on the letter ───────────────────────────────────
const noteEl = ref(null)
function fitNote () {
  const el = noteEl.value
  if (!el || !el.style) return
  el.style.height = 'auto'
  if (el.scrollHeight) el.style.height = `${el.scrollHeight}px`
}
function focusNote () {
  const el = noteEl.value
  if (!el) return
  try { el.focus(); const n = el.value.length; el.setSelectionRange(n, n) } catch (e) { /* */ }
}
watch(cpNote, () => nextTick(fitNote))
watch(wide, () => nextTick(fitNote))
// The kindness gate (R4.46): the server's "That didn't read as kind…" under
// the note. The text stays; editing clears it and the edit is judged again.
const cpNoteError = ref('')
// The note the server last judged and stored (the Mystic's draft counts).
const cpNoteCommitted = ref((g.value.gift_note || '').trim())
const cpNoteUncommitted = computed(() => {
  const note = cpNote.value.trim()
  return !!note && note !== cpNoteCommitted.value
})
function cpNoteRefused (e) {
  const refused = profileFieldRejection(e)
  return refused && refused.field === 'note' ? refused.message : ''
}

// ── The first send of a guest: the password moment (R7.11) ────────────────
const pwMoment = computed(() => passwordStep(g.value.password_moment))
const pwFor = ref('')            // '' | 'send' | 'contact' | 'link'
const pwShare = ref(false)
const pwSms = ref('')
const pwAnswered = ref(false)
function needsPassword (action) {
  if (!pwMoment.value.ask || pwAnswered.value) return false
  pwFor.value = action
  return true
}
function onPasswordDone ({ outcome } = {}) {
  pwAnswered.value = true
  const action = pwFor.value
  pwFor.value = ''
  if (outcome === 'saved' || outcome === 'pending') g.value = { ...g.value, password_moment: { ask: false, email: false } }
  if (action === 'link') cpCopyLink({ share: pwShare.value, sms: pwSms.value })
  else if (action === 'contact') sendContact()
  else cpSend()
}

// ── An address that asked us not to send (R10.8) ──────────────────────────
const cpBlockedLine = ref('')
// "✦ This one's on us" (mystic-page 2026-10-29, API.md §9): the server's
// { line, sub } only when this send is free for someone new; null otherwise.
const cpOnUs = ref(null)
// "♡ Ask {Name} to join my Circle": unticked, for every typed address.
const askCircle = ref(false)
let cpCheckTimer = null
let cpCheckSeq = 0
async function cpCheckContact (contact) {
  const seq = ++cpCheckSeq
  const value = (contact || '').trim()
  if (!value) { cpBlockedLine.value = ''; cpOnUs.value = null; return }
  try {
    // { blocked, line, kind, contact, sendable, reason } (build/API.md §5),
    // with new_person and on_us for the gift being sent (mystic-page API.md §9).
    const r = await careContactCheck(sid.value, value, rid.value)
    if (seq !== cpCheckSeq) return
    cpBlockedLine.value = (r && r.blocked && r.line) || ''
    cpOnUs.value = (r && !r.blocked && r.on_us && r.on_us.line) ? r.on_us : null
  } catch (e) { if (seq === cpCheckSeq) { cpBlockedLine.value = ''; cpOnUs.value = null } }
}
watch(contactValue, (v) => {
  clearTimeout(cpCheckTimer)
  // A phone goes from the sender's own phone: nothing to ask the server.
  if (!v || toKind.value === 'phone') { cpCheckSeq++; cpBlockedLine.value = ''; cpOnUs.value = null; return }
  cpCheckTimer = setTimeout(() => cpCheckContact(v), 350)
})
const blocked = computed(() => lane.value === 'email' && !!cpBlockedLine.value)
// The two show once a whole address is in the box, before anything goes out;
// a pick from the circle is already in it, so neither shows for a member.
const typedTo = computed(() => !wentOut.value && !member.value && lane.value !== 'phone' && !!contactValue.value && !blocked.value)
const onUsShown = computed(() => typedTo.value && !!cpOnUs.value)
const askCircleShown = computed(() => typedTo.value)

// ── Send: to a member's Messages, or by our email ─────────────────────────
async function deliver (contact) {
  const sent = await careSend(sid.value, rid.value, { contact, note: cpNote.value.trim(), signed_as: cpSigned.value,
    ask_circle: !!(askCircle.value && askCircleShown.value) })
  if (sent && sent.password_moment) g.value = { ...g.value, password_moment: sent.password_moment }
  g.value = { ...g.value, sent: true, invite: (sent && sent.invite) || g.value.invite }
  handedOver.value = true
  clearDraft(rid.value)
  emit('sent', { for_name: g.value.for_name, for_querent: g.value.for_querent, sent: true,
                 invite: (sent && sent.invite) || null, opened: !!(sent && sent.opened) })
  return sent
}
// Short of carats (a 402, e2e round 2): how many more, and a gold "Get carats"
// in place of the send. The note and the address stay where they are.
const cpShort = ref(null)
function getCarats () {
  cpShort.value = null
  emit('need-carats')
}
function failed (e) {
  const unkind = cpNoteRefused(e)
  if (unkind) { cpStatus.value = ''; cpNoteError.value = unkind; return }
  const short = sendShortOf(e)
  if (short) { cpStatus.value = ''; cpShort.value = short; return }
  cpStatus.value = sendErrorReason(e)
  const said = sendErrorMessage(e)
  if (cpStatus.value === 'opted_out') { cpBlockedLine.value = said; cpStatus.value = ''; return }
  cpError.value = said
}
async function cpSend () {
  if (cpStatus.value === 'sending' || drafting.value || cpNoteError.value || done.value) return
  if (needsPassword('send')) return
  cpStatus.value = 'sending'
  cpError.value = ''
  try {
    await deliver('')
    cpStatus.value = ''
    done.value = 'sent'
    trackShare('care_package', 'reading', rid.value, { delivery: 'member' })
    closeSoon()
  } catch (e) { failed(e) }
}
async function sendContact () {
  if (cpStatus.value === 'sending' || drafting.value || cpNoteError.value || blocked.value || done.value) return
  const kind = lane.value
  const contact = contactValue.value
  if (!contact) {
    contactHint.value = kind === 'phone' ? 'Add the whole number.' : `Add ${possessive(name.value)} email address.`
    try { toBoxEl.value && toBoxEl.value.focus() } catch (e) { /* */ }
    return
  }
  if (needsPassword('contact')) return
  cpStatus.value = 'sending'
  cpError.value = ''
  try {
    await deliver(contact)
    cpStatus.value = ''
    done.value = 'sent'
    // A typed address may belong to a member (it went to their Messages): the
    // screen says the same either way.
    trackShare('care_package', 'reading', rid.value, { delivery: kind === 'phone' ? 'sms' : 'email' })
    closeSoon()
  } catch (e) { failed(e) }
}

// ── Share from the phone, or copy the link (R4.39) ────────────────────────
// The short link (/g/<code>) is made when this screen opens, so the tap
// shares or copies it at once, inside the gesture. "Made" means handed over:
// the server's hand-over (careLink) runs after the share or copy succeeds.
const cpPrepared = ref(null)      // { link, first_line, title } from prepare
const cpLink = ref(null)          // { link, message } once handed over
const cpLinkState = ref('')       // '', 'making', 'copied', 'shared', 'failed'
const cpFallback = ref(false)     // sharing and copying both failed
const cpMessageCopied = ref(false)
const fallbackInput = ref(null)
let cpPrepareSeq = 0
async function cpPrepare () {
  const seq = ++cpPrepareSeq
  const note = cpNote.value.trim()
  try {
    const r = await carePrepareLink(sid.value, rid.value, note && !wentOut.value ? { note } : {})
    if (note) cpNoteCommitted.value = note
    if (r && r.link) cpPrepared.value = r
    return { ok: !!(r && r.link) }
  } catch (e) {
    const unkind = cpNoteRefused(e)
    if (unkind && seq === cpPrepareSeq && cpNote.value.trim() === note) cpNoteError.value = unkind
    return { ok: false, unkind, error: e }
  }
}
// An edit clears the message and, after a pause, commits the new words to
// the prepared link (judged there).
let cpNoteTimer = null
watch(cpNote, () => {
  cpNoteError.value = ''
  clearTimeout(cpNoteTimer)
  if (!wentOut.value && cpNoteUncommitted.value) cpNoteTimer = setTimeout(cpPrepare, 600)
})
function cpSharePackage (link) {
  const p = cpPrepared.value || {}
  return { link, text: giftShareText(p.first_line, cpNote.value, link), title: p.title || '' }
}
// The tap. Nothing may await before handOver: it opens the sheet or writes
// the clipboard in this same call stack. via: 'primary' | 'secondary'.
// sms: a typed phone (E.164) on a phone; Messages opens to it.
const howFor = (share, sms) => (sms ? { share, sms } : { share })
function cpCopyLink ({ share = canShareSheet, via = 'primary', sms = '' } = {}) {
  if (cpLinkState.value === 'making' || cpNoteError.value || drafting.value) return
  pwShare.value = share
  pwSms.value = sms
  if (needsPassword('link')) return
  cpError.value = ''
  const ready = (cpLink.value && cpLink.value.link) || (cpPrepared.value && cpPrepared.value.link)
  // Words the server has not judged yet take the slow way, judged first.
  if (!ready || (!wentOut.value && cpNoteUncommitted.value)) return cpCopyLinkSlow({ share, via, sms })
  return cpFinish(handOver(cpSharePackage(ready), howFor(share, sms)), via)
}
async function cpFinish (pending, via = 'primary') {
  const outcome = await pending
  if (outcome === 'cancelled') return            // stay exactly as it was
  cpLinkState.value = outcome
  if (outcome === 'failed') {
    cpFallback.value = true
    nextTick(() => { try { fallbackInput.value && (fallbackInput.value.focus(), fallbackInput.value.select()) } catch (e) { /* */ } })
    await cpHandOverToServer()
    return
  }
  cpFallback.value = false
  if (outcome === 'copied') { if (via === 'secondary') secDone.value = 'copied'; else done.value = 'copied' }
  // Shared, copied, or shown to copy by hand: it is going to them.
  const ok = await cpHandOverToServer()
  // The phone's own sheet is the confirmation: once it returns, the screen goes.
  if (outcome === 'shared' && ok) emit('close')
}
let cpHandingOver = null
async function cpHandOverToServer () {
  if (cpLink.value) return true
  if (cpHandingOver) return cpHandingOver
  cpHandingOver = cpHandOverNow().finally(() => { cpHandingOver = null })
  return cpHandingOver
}
// Two calls, sent together: the quick one makes (or reuses) the invite and
// answers at once; the full one writes the close and their version if the
// screen's preview has not yet.
async function cpHandOverNow () {
  const fields = { note: cpNote.value.trim(), signed_as: cpSigned.value }
  const full = careLink(sid.value, rid.value, fields)
  full.then((f) => { if (f && f.message) cpLink.value = { link: f.link, message: f.message } }).catch(() => { /* the quick one speaks */ })
  try {
    const r = await careLink(sid.value, rid.value, { ...fields, quick: true })
    cpLink.value = cpLink.value || { link: r.link, message: r.message }
    handedOver.value = true
    clearDraft(rid.value)
    g.value = { ...g.value, sent: true, invite: r.invite || g.value.invite }
    trackShare('care_package', 'reading', rid.value, { delivery: 'link' })
    const { link, message, ...gift } = r   // eslint-disable-line no-unused-vars
    emit('linked', gift)
    return true
  } catch (e) {
    const unkind = cpNoteRefused(e)
    // Refused before it went: back to the letter, the message under the note.
    if (unkind) { cpNoteError.value = unkind; done.value = ''; secDone.value = ''; cpFallback.value = false; cpLinkState.value = '' }
    else cpError.value = sendErrorMessage(e)
    return false
  }
}
async function cpCopyLinkSlow ({ share, via, sms = '' }) {
  clearTimeout(cpNoteTimer)
  cpLinkState.value = 'making'
  const made = await cpPrepare()
  if (!made.ok) {
    cpLinkState.value = ''
    if (!made.unkind) cpError.value = sendErrorMessage(made.error)
    return
  }
  cpLinkState.value = ''
  return cpFinish(handOver(cpSharePackage(cpPrepared.value.link), howFor(share, sms)), via)
}
async function cpCopyMessage () {
  if (!cpLink.value) return
  cpMessageCopied.value = await copyText(cpLink.value.message)
}
const fallbackLink = computed(() => (cpLink.value && cpLink.value.link) || (cpPrepared.value && cpPrepared.value.link) || '')

// ── The buttons ───────────────────────────────────────────────────────────
const busy = computed(() => cpStatus.value === 'sending' || cpLinkState.value === 'making')
const primaryDisabled = computed(() => busy.value || drafting.value || !!cpNoteError.value)
const primaryText = computed(() => {
  if (cpStatus.value === 'sending') return 'Sending…'
  if (cpLinkState.value === 'making') return 'One moment…'
  return primary.value.label
})
// A typed phone: the sender's own Messages (a phone) or the link to paste (a computer).
function textOwn () {
  const number = contactValue.value
  if (!number) {
    contactHint.value = 'Add the whole number.'
    try { toBoxEl.value && toBoxEl.value.focus() } catch (e) { /* */ }
    return
  }
  return cpCopyLink({ share: canShareSheet, sms: canShareSheet ? number : '' })
}
function onPrimary () {
  if (lane.value === 'member') return cpSend()
  if (lane.value === 'phone') return textOwn()
  if (lane.value === 'email') return sendContact()
  // Copied already: copying again is the same link (one invite).
  return cpCopyLink({ share: lane.value === 'share' })
}
function onSecondary () {
  if (!secondary.value) return
  return cpCopyLink({ share: secondary.value.share, via: 'secondary' })
}

function onKey (e) {
  if (e.key !== 'Escape' || pwFor.value) return
  if (signOpen.value) { signOpen.value = false; signTyping.value = false; return }
  if (renaming.value) { renaming.value = false; return }
  if (previewOpen.value) { previewOpen.value = false; return }
  emit('close')
}
// The letter's faces load after the screen opens, and a phone turns: the
// note's height follows, so no line is ever hidden.
function onResize () { fitNote() }
onMounted(async () => {
  markHintSeen()
  if (typeof document !== 'undefined') {
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDocDown, true)
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize)
    try {
      wideQuery = window.matchMedia ? window.matchMedia(WIDE) : null
      onWide()
      if (wideQuery && wideQuery.addEventListener) wideQuery.addEventListener('change', onWide)
    } catch (e) { wideQuery = null }
  }
  try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => nextTick(fitNote)) } catch (e) { /* */ }
  nextTick(fitNote)
  // Their version and the first message, written now; the short link, made
  // now so the tap can hand it over at once (the secondary uses it too).
  loadPreview()
  cpPrepare()
  if (!wentOut.value) loadFriends()
  if (!cpNote.value && !alreadySent) {
    cpStatus.value = 'drafting'
    try {
      const r = await careDraft(sid.value, rid.value)
      if (r && r.gift_note) { cpNoteCommitted.value = r.gift_note.trim(); cpNote.value = r.gift_note; g.value = { ...g.value, ...r } }
    } catch (e) { /* the note stays empty; they write their own */ }
    cpStatus.value = ''
  }
})
onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('keydown', onKey)
    document.removeEventListener('mousedown', onDocDown, true)
  }
  if (typeof window !== 'undefined') window.removeEventListener('resize', onResize)
  try { if (wideQuery && wideQuery.removeEventListener) wideQuery.removeEventListener('change', onWide) } catch (e) { /* */ }
  clearTimeout(cpNoteTimer); clearTimeout(cpCheckTimer); clearTimeout(closeTimer)
})
</script>

<template>
  <Teleport to="body">
    <!-- A letter: a click outside never closes it (the X does). -->
    <div class="gsend-overlay" data-testid="gift-send-overlay">
      <section v-show="!(previewOpen && !wide)" class="gsend" :class="{ wide, 'no-pic': !image, 'went-out': wentOut }"
               role="dialog" aria-modal="true" :aria-label="`A gift for ${name}`" data-testid="care-share" :data-lane="lane">
        <span class="gs-grab" aria-hidden="true"></span>

        <!-- A computer: the letter as they will open it, read-only, mirroring the note. -->
        <div v-if="wide" class="gs-pane" data-testid="gift-pane">
          <div class="gs-pane-scroll">
            <GiftPreview variant="pane" :preview="preview" :image="image" :note="cpNote" :from-name="fromName" :for-name="name" />
          </div>
        </div>

        <div class="gs-main">
          <!-- The header: "A gift for / <Name>", ⓘ, ✕ -->
          <header class="gs-head" data-testid="care-who">
            <div class="gs-title">
              <span class="gs-for">A gift for</span>
              <input v-if="renaming" ref="renameInput" v-model="renameText" class="gs-rename-input" data-testid="care-rename-input"
                     aria-label="Who the gift is for" maxlength="60" @keydown.enter.prevent="commitRename" @blur="commitRename" />
              <button v-else-if="!wentOut" type="button" class="gs-name gs-name-edit" data-testid="care-to-name"
                      :aria-label="`${name}. Rename the gift`" @click="startRename">{{ name }}</button>
              <span v-else class="gs-name" data-testid="care-to-name">{{ name }}</span>
            </div>
            <HelpPopover :lines="helpLines" />
            <button type="button" class="gs-x" aria-label="Close" data-testid="care-close-send" @click="emit('close')">
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </header>

          <!-- To: the circle, an email or a phone; one token. -->
          <div v-if="showTo" class="gs-to">
            <ToBox ref="toBoxEl" v-model:token="toToken" v-model:text="toText" :friends="circle"
                   :disabled="cpStatus === 'sending' || done === 'sent' || (alreadySent && member)"
                   :placeholder="wide ? 'Name from your circle, email or phone' : 'Name, email or phone'"
                   @picked="onPicked" @cleared="onCleared" />
            <p v-if="hints && !wentOut && !toToken && !toText" class="gs-hint" data-testid="hint-to">
              <span class="gs-star" aria-hidden="true">✦</span><span>{{ TO_HINT }}</span>
            </p>
            <p v-if="contactHint" class="gs-hint-line" role="status" data-testid="care-contact-hint">{{ contactHint }}</p>
            <!-- Someone new (API.md §9): the one line, and its quiet carrot. Nothing for anyone else. -->
            <div v-if="onUsShown" class="gs-onus" role="status" data-testid="care-on-us">
              <p class="gs-onus-main"><span class="gs-onus-star" aria-hidden="true">✦</span><span class="gs-onus-line">{{ cpOnUs.line }}</span></p>
              <p v-if="cpOnUs.sub" class="gs-onus-sub">{{ cpOnUs.sub }}</p>
            </div>
            <!-- For every typed address, so it never tells who has an account. -->
            <label v-if="askCircleShown" class="gs-ask" data-testid="care-ask-circle">
              <input v-model="askCircle" type="checkbox" class="gs-ask-box" />
              <span class="gs-ask-mark" aria-hidden="true"></span>
              <span class="gs-ask-text"><span aria-hidden="true">♡</span> Ask {{ name }} to join my Circle</span>
            </label>
          </div>

          <!-- No rating here (decision 12, 2026-10-26, gift bug #23): a reading
               for someone is rated in the chat's end block after the send. -->

          <!-- The note on its paper card (on a phone, over the picture). -->
          <div class="gs-env">
            <div v-if="image && !wide" class="gs-pic"><img :src="image" alt="" /></div>
            <div class="gs-paper">
              <span class="gs-wax" aria-hidden="true">✦</span>
              <div ref="signRoot" class="gs-eyebrow">
                <button v-if="!wentOut" type="button" class="gs-sign" data-testid="care-sign-as" :aria-expanded="signOpen ? 'true' : 'false'"
                        aria-haspopup="listbox" :aria-label="`Signed ${fromName}. Sign it another way`" @click="toggleSign">
                  <span class="gs-sign-name">{{ fromName }}</span><i class="fas fa-chevron-down" aria-hidden="true"></i>
                </button>
                <span v-else class="gs-sign-read">{{ fromName }}</span>
                <span class="gs-eyebrow-rest">was thinking of you</span>
                <div v-if="signOpen" class="gs-sign-menu" data-testid="care-sign-menu">
                  <p class="gs-sign-title">Sign as</p>
                  <ul role="listbox" aria-label="Sign as">
                    <li v-for="n in signChoices" :key="n" role="option" :aria-selected="n === fromName ? 'true' : 'false'"
                        :class="{ on: n === fromName }" @click="pickSign(n)">
                      <span>{{ n }}</span><i v-if="n === fromName" class="fas fa-check" aria-hidden="true"></i>
                    </li>
                  </ul>
                  <input v-if="signTyping" ref="signInput" v-model="signText" class="gs-sign-input" data-testid="care-sign-input"
                         aria-label="Sign it another way" maxlength="60" placeholder="Your name, as they know you"
                         @keydown.enter.prevent="commitSign" />
                  <button v-else type="button" class="gs-sign-other" data-testid="care-sign-other" @click="signAnotherWay">Sign another way…</button>
                </div>
              </div>
              <div v-if="wide && !wentOut" class="gs-note-label"><label for="cp-note">Your note</label><span>they read it first</span></div>
              <div v-if="!wentOut" class="gs-note-box">
                <textarea id="cp-note" ref="noteEl" class="cp-note gs-note" v-model="cpNote" rows="3"
                          :aria-label="`Your note to ${name}`" :placeholder="drafting ? 'Mystic Aida is writing a line…' : 'Say something in your own words'"
                          :aria-invalid="cpNoteError ? 'true' : null" :aria-describedby="cpNoteError ? 'cp-note-err' : null"
                          @input="fitNote"></textarea>
                <button type="button" class="gs-pen" aria-label="Edit your note" tabindex="-1" @click="focusNote">
                  <i class="fas fa-pen" aria-hidden="true"></i>
                </button>
              </div>
              <p v-else class="gs-note-read" data-testid="care-note-sent">{{ cpNote }}</p>
              <p v-if="cpNoteError" id="cp-note-err" class="gs-note-err" role="alert" data-testid="care-note-error" data-field-error="note">{{ cpNoteError }}</p>
              <p v-else-if="hints && !wentOut && !drafting" class="gs-hint gs-hint-note" data-testid="hint-note">
                <span class="gs-star" aria-hidden="true">✦</span><span>Mystic Aida drafted this from your reading. <b>Make it yours</b>: change any word.</span>
              </p>
            </div>
          </div>

          <!-- A phone: what they open, and the way in. -->
          <button v-if="!wide" type="button" class="gs-peek" data-testid="care-peek" @click="openPreview">
            <span class="gs-fan" aria-hidden="true" :style="{ '--n': peekCards.length || 4 }">
              <template v-if="peekCards.length">
                <img v-for="(c, i) in peekCards" :key="i" :src="c.image_url || ''" alt="" :class="{ rev: c.reversed }" :style="{ '--i': i }" />
              </template>
              <template v-else>
                <span v-for="i in 4" :key="'b' + i" class="gs-fan-blank" :style="{ '--i': i - 1 }"></span>
              </template>
            </span>
            <span class="gs-peek-words">
              <span class="gs-peek-eyebrow">What {{ name }} opens</span>
              <span v-if="peekTitle" class="gs-peek-title">{{ peekTitle }}</span>
              <span v-else class="gs-peek-writing">Mystic Aida is writing {{ possessive(name) }} version…</span>
              <span class="gs-peek-go">Read it as they will <i class="fas fa-chevron-right" aria-hidden="true"></i></span>
            </span>
          </button>

          <SendPassword v-if="pwFor" :ask-email="pwMoment.email" :session-id="sid"
                        :action-label="pwFor === 'link' ? 'send it' : 'send'" @done="onPasswordDone" />
          <template v-else-if="!cpFallback">
            <!-- An address that asked us not to send: said in place of the button. -->
            <p v-if="blocked" class="gs-blocked" role="status" data-testid="care-opted-out">{{ cpBlockedLine }}</p>
            <!-- Short of carats: how many more, and the store (or, before carats show, the Quest). -->
            <template v-else-if="cpShort">
              <p class="gs-short" role="status" data-testid="care-short">{{ cpShort.line }}</p>
              <button type="button" class="gs-go gs-go-gold" data-testid="care-get-carats" @click="getCarats">
                <span>{{ cpShort.visible ? 'Get carats' : 'Open Quest' }}</span>
              </button>
            </template>
            <button v-else type="button" class="gs-go" :class="{ done: primary.done }" data-testid="care-send"
                    :disabled="primaryDisabled" :aria-busy="busy ? 'true' : null" :aria-live="primary.done ? 'polite' : null" @click="onPrimary">
              <span v-if="primary.done" class="gs-go-seal" aria-hidden="true"><i class="fas fa-check"></i></span>
              <span v-else-if="busy" class="gs-go-seal gs-go-breathe" aria-hidden="true">✦</span>
              <i v-else :class="['fas', primary.icon]" aria-hidden="true"></i>
              <span>{{ primaryText }}</span>
            </button>
            <p v-if="copiedSaid" class="gs-copied" role="status" data-testid="care-copied">{{ copiedLine(name, { lane }) }}</p>
            <div class="gs-row" :class="{ solo: !secondary }">
              <button v-if="secondary" type="button" class="gs-second" :class="{ done: secDone === 'copied' }" data-testid="care-secondary"
                      :disabled="busy || drafting || !!cpNoteError" @click="onSecondary">
                <i :class="['fas', secondary.icon]" aria-hidden="true"></i><span>{{ secondary.label }}</span>
              </button>
              <p class="gs-lock" data-testid="care-lock"><i class="fas fa-lock" aria-hidden="true"></i>{{ lockLine(name) }}</p>
            </div>
          </template>
          <p v-if="cpError" class="gs-err" role="alert" data-testid="care-error">{{ cpError }}</p>

          <!-- Sharing and copying both failed: the link, selected, to copy by hand (R4.53). -->
          <div v-if="cpFallback" class="gs-fallback" data-testid="care-link">
            <button type="button" class="gs-go" data-testid="care-try-again" @click="cpCopyLink({ share: canShareSheet })">
              <i class="fas fa-gift" aria-hidden="true"></i><span>Try again</span>
            </button>
            <div class="gs-fb-panel">
              <p class="gs-fb-say">{{ canShareSheet ? "Your phone didn't share or copy it." : "Your browser didn't copy it." }} Press the link to copy it yourself.</p>
              <input ref="fallbackInput" class="gs-fb-url" data-testid="care-link-url" :value="fallbackLink" readonly
                     aria-label="The link to paste" @focus="$event.target.select()" @click="$event.target.select()" />
              <template v-if="cpLink && cpLink.message">
                <p class="gs-fb-label">Or copy it with a few words</p>
                <textarea class="gs-fb-msg cp-linkmsg" :value="cpLink.message" readonly rows="6" aria-label="The message, with the link"
                          @focus="$event.target.select()"></textarea>
                <button type="button" class="gs-fb-copy" data-testid="care-copy-message" @click="cpCopyMessage">
                  <i :class="cpMessageCopied ? 'fas fa-check' : 'far fa-copy'" aria-hidden="true"></i>
                  {{ cpMessageCopied ? 'Message copied' : 'Copy the message' }}
                </button>
              </template>
            </div>
            <p class="gs-lock" data-testid="care-lock"><i class="fas fa-lock" aria-hidden="true"></i>{{ lockLine(name) }}</p>
          </div>
        </div>
      </section>

      <!-- A phone: the preview is a sheet inside the modal, never a page change. -->
      <section v-if="previewOpen && !wide" class="gsend gs-sheet" role="dialog" aria-modal="true"
               :aria-label="`What ${name} receives`" data-testid="care-preview-sheet">
        <span class="gs-grab" aria-hidden="true"></span>
        <GiftPreview variant="sheet" :preview="preview" :image="image" :note="cpNote" :from-name="fromName" :for-name="name"
                     @back="previewOpen = false" />
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.gsend-overlay {
  --ink: #2a1845; --ink2: #4a3a63; --muted: #6b5a82; --quiet: #8a7a9f; --amethyst: #4e2776; --violet: #7b4bb0;
  --cream: #fbf6ec; --paper: #fffdf8; --paper-edge: #e4d6b8; --gold-dk: #8a6a2a; --gold-lt: #e7c983;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  position: fixed; inset: 0; z-index: 1200; display: flex; align-items: center; justify-content: center;
  padding: 20px; box-sizing: border-box;
  /* The room behind is night: dark enough that none of the reading's own
     controls (its X) reads as a second way out. */
  background-color: rgba(9, 4, 22, 0.94);
  -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
}
.gsend {
  position: relative; box-sizing: border-box; display: flex; flex-direction: column;
  width: 100%; max-width: 520px; max-height: calc(100dvh - 40px); overflow-x: clip; overflow-y: auto; overscroll-behavior: contain;
  padding: 22px 26px 22px; border-radius: 22px; background: var(--cream); color: var(--ink); font-family: var(--ui);
  box-shadow: 0 40px 80px -30px #000; text-align: left;
}
/* Everything stacks under the button: no footer, nothing pinned to the foot. */
.gs-main { display: flex; flex-direction: column; flex: 0 0 auto; min-width: 0; }
.gs-grab { display: none; }

/* A computer: the letter on the left, the composer on the right. The
   composer sets the height; the letter scrolls inside its column. */
.gsend.wide { flex-direction: row; max-width: 1040px; min-height: min(600px, calc(100dvh - 40px)); padding: 0; overflow: hidden; }
.gsend.wide .gs-pane { position: relative; flex: 0 0 54%; min-width: 0; background: #f1e8d6; border-right: 1px solid var(--paper-edge); }
.gsend.wide .gs-pane-scroll { position: absolute; inset: 0; overflow-y: auto; overscroll-behavior: contain; padding: 22px 22px 26px; box-sizing: border-box; }
.gsend.wide .gs-main { flex: 1 1 auto; overflow-y: auto; overscroll-behavior: contain; padding: 26px 28px 24px; align-self: flex-start;
  max-height: calc(100dvh - 40px); box-sizing: border-box; }

/* The header */
.gs-head { display: flex; align-items: flex-start; gap: 10px; margin: 0 0 16px; min-width: 0; }
.gs-title { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; padding-top: 2px; }
.gs-for { font: 500 14px/1.3 var(--ui); color: var(--muted); }
.gs-name { align-self: flex-start; max-width: 100%; padding: 0 0 2px; border: 0; background: none; text-align: left;
  font: 600 25px/1.2 var(--display); color: var(--ink); letter-spacing: 0.01em; overflow-wrap: anywhere;
  text-decoration: underline dotted rgba(78, 39, 118, 0.45); text-decoration-thickness: 1.5px; text-underline-offset: 6px; }
/* A finger gets 44px without moving the header. */
@media (pointer: coarse) { .gs-name-edit { min-height: 44px; margin: -8px 0; } }
.gs-name-edit { cursor: pointer; }
.gs-name-edit:hover { color: var(--amethyst); }
.gs-rename-input { min-height: 44px; box-sizing: border-box; width: 100%; padding: 6px 10px; border-radius: 10px; border: 1.5px solid var(--violet);
  background: #fff; color: var(--ink); font: 600 20px var(--display); }
.gs-rename-input:focus { outline: none; box-shadow: 0 0 0 3px rgba(123, 75, 176, 0.15); }
.gs-x {
  flex: none; width: 44px; height: 44px; border-radius: 50%; border: 0;
  display: grid; place-items: center; background: #efe5d2; color: var(--amethyst); font-size: 15px; cursor: pointer;
}
.gs-x:hover { background: #e8dcc4; }

/* Hints (the first two opens) and feedback */
.gs-hint { display: flex; gap: 8px; margin: 10px 2px 0; font: 500 13.5px/1.45 var(--ui); color: var(--ink2); text-wrap: pretty; }
.gs-hint b { color: var(--amethyst); font-weight: 700; }
.gs-star { flex: none; color: #c9a94e; font-size: 12px; line-height: 1.6; }
.gs-hint-line { margin: 8px 2px 0; font: 600 13px/1.4 var(--ui); color: #a5531f; }

/* The note on its paper card */
.gs-env { position: relative; margin: 26px 0 0; flex: none; }
.gs-pic { height: 150px; border-radius: 16px; overflow: hidden; background: #2a1845; }
.gs-pic img { display: block; width: 100%; height: 100%; object-fit: cover; }
.gs-paper {
  position: relative; z-index: 2; padding: 26px 16px 14px; border-radius: 16px; background: var(--paper);
  border: 1px solid var(--paper-edge); box-shadow: 0 16px 28px -20px rgba(42, 24, 69, 0.55);
}
.gs-pic + .gs-paper { margin: -64px 10px 0; }
.gs-wax {
  position: absolute; top: -16px; left: 50%; transform: translateX(-50%); width: 32px; height: 32px; border-radius: 50%;
  display: grid; place-items: center; color: #5a3f0f; font: 700 13px var(--ui);
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a 100%);
  box-shadow: 0 3px 8px rgba(42, 24, 69, 0.45), inset 0 0 0 3px rgba(255, 240, 200, 0.35);
}
.gs-eyebrow { position: relative; display: flex; flex-wrap: wrap; justify-content: center; align-items: baseline; column-gap: 8px;
  margin: 0 0 12px; text-align: center; font: 600 11px/1.4 var(--display); letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold-dk); }
.gs-sign { display: inline-flex; align-items: center; gap: 5px; min-height: 0; padding: 3px 2px 1px; border: 0; background: none; cursor: pointer;
  font: inherit; letter-spacing: inherit; text-transform: inherit; color: var(--amethyst); }
.gs-sign-name { text-decoration: underline dotted rgba(78, 39, 118, 0.55); text-decoration-thickness: 1.5px; text-underline-offset: 5px; }
.gs-sign i { font-size: 9px; letter-spacing: 0; }
/* A finger gets 44px without moving the line: the extra height overlaps the space around it. */
@media (pointer: coarse) { .gs-sign { min-height: 44px; margin: -11px 0; } }
.gs-sign-read { color: var(--gold-dk); }
.gs-sign-menu { position: absolute; z-index: 30; top: calc(100% + 6px); left: 50%; transform: translateX(-50%); width: min(300px, 82vw);
  padding: 10px 8px 8px; box-sizing: border-box; border-radius: 16px; background: #fff; border: 1px solid #e3d4f5;
  box-shadow: 0 20px 40px -18px rgba(42, 24, 69, 0.55); text-align: left; text-transform: none; letter-spacing: 0; }
.gs-sign-title { margin: 2px 10px 6px; font: 600 10.5px var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-dk); }
.gs-sign-menu ul { list-style: none; margin: 0; padding: 0; }
.gs-sign-menu li { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 44px; padding: 0 12px; border-radius: 10px;
  cursor: pointer; font: 500 15px var(--ui); color: var(--ink); }
.gs-sign-menu li:hover { background: #f6f1fb; }
.gs-sign-menu li.on { background: #f1ebfa; font-weight: 700; color: #5a2f88; }
.gs-sign-menu li i { color: var(--violet); font-size: 12px; }
.gs-sign-other { display: block; width: 100%; min-height: 44px; margin-top: 4px; padding: 0 12px; border: 0; border-top: 1px solid #ece4f6;
  background: none; text-align: left; cursor: pointer; font: 600 15px var(--ui); color: var(--amethyst); }
.gs-sign-input { display: block; width: 100%; box-sizing: border-box; min-height: 44px; margin-top: 6px; padding: 8px 12px; border-radius: 10px;
  border: 1.5px solid var(--violet); font: 500 16px var(--ui); color: var(--ink); }
.gs-sign-input:focus { outline: none; box-shadow: 0 0 0 3px rgba(123, 75, 176, 0.15); }
.gs-note-label { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin: 0 2px 8px; }
.gs-note-label label { font: 600 13.5px var(--ui); color: var(--ink); }
.gs-note-label span { font: 500 12.5px var(--ui); color: var(--muted); }
.gs-note-box { position: relative; }
.gs-note {
  display: block; width: 100%; box-sizing: border-box; margin: 0; padding: 12px 46px 12px 16px; min-height: calc(3 * 1.45em + 26px);
  resize: none; overflow: hidden; border: 1.5px solid #d9c9ef; border-radius: 14px; background: #fff; color: var(--ink);
  font: italic 500 19px/1.45 var(--serif); text-wrap: pretty; overflow-wrap: break-word;
}
.gs-note:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 4px rgba(123, 75, 176, 0.15); }
.gs-note::placeholder { color: #a596b8; }
.gs-note[aria-invalid="true"] { border-color: #b4622a; }
.gs-pen { position: absolute; top: 10px; right: 10px; width: 30px; height: 30px; border-radius: 50%; border: 0; background: #f1ebfa;
  color: var(--violet); font-size: 11px; display: grid; place-items: center; cursor: text; }
/* A 44px hit area round the 30px pencil (e2e phone walk 2026-10-30). */
.gs-pen::after { content: ''; position: absolute; inset: -7px; border-radius: 50%; }
/* On touch the pencil itself is 44px (e2e round 2): drawn at 30 in the same place. */
@media (pointer: coarse) {
  .gs-pen { top: 3px; right: 3px; width: 44px; height: 44px; padding: 7px; box-sizing: border-box; background-clip: content-box; }
  .gs-pen::after { content: none; }
}
.gs-note-read { margin: 0; white-space: pre-line; font: italic 500 19px/1.45 var(--serif); color: var(--ink); text-align: center; }
.gs-hint-note { margin: 12px 2px 0; }
.gs-note-err { margin: 10px 2px 0; font: 600 13px/1.45 var(--ui); color: #a5531f; }

/* What they open (a phone): the cards fanned, the title, the way in. */
.gs-peek {
  display: flex; align-items: center; gap: 16px; width: auto; margin: -10px 10px 0; padding: 24px 16px 16px; box-sizing: border-box;
  border-radius: 0 0 16px 16px; border: 1px solid var(--paper-edge); border-top: 0; background: #f3ead8; cursor: pointer; text-align: left;
  color: var(--ink); font-family: var(--ui);
}
.gsend.no-pic .gs-peek { margin-left: 8px; margin-right: 8px; }
.gs-peek:hover { background: #efe4cf; }
.gs-fan { position: relative; flex: none; width: 112px; height: 92px; }
.gs-fan img, .gs-fan-blank {
  position: absolute; bottom: 0; left: calc(var(--i) * var(--step, 22px)); width: 44px; aspect-ratio: 2 / 3; object-fit: cover; border-radius: 5px;
  border: 1.5px solid #fff; background: #2a1845; box-shadow: 0 6px 12px -6px rgba(42, 24, 69, 0.7);
  transform: rotate(calc((var(--i) - 1.5) * 6deg)); transform-origin: 50% 120%;
}
/* A reversed card turns over in its own place. Turned about the fan's pivot
   (120% down) it dropped a card's height out of the peek, over the password
   ask under it (gift bug walk 2026-10-25 #22): the pivot is moved to the
   card's centre for the half turn, then back. */
.gs-fan img.rev { transform: rotate(calc((var(--i) - 1.5) * 6deg)) translateY(-70%) rotate(180deg) translateY(70%); }
.gs-fan-blank { background: linear-gradient(160deg, #4e2776, #2a1845); }
.gs-peek-words { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.gs-peek-eyebrow { font: 600 10.5px/1.4 var(--display); letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-dk); }
.gs-peek-title { font: 600 15.5px/1.25 var(--display); color: var(--ink); text-wrap: balance; overflow-wrap: anywhere; }
.gs-peek-writing { font: italic 500 15px/1.35 var(--serif); color: var(--muted); }
.gs-peek-go { margin-top: 2px; font: 700 14px/1.3 var(--ui); color: var(--amethyst); }
.gs-peek-go i { font-size: 10px; margin-left: 4px; }

/* The primary. */
.gs-go {
  display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; box-sizing: border-box; margin: 22px 0 0;
  min-height: 56px; padding: 12px 22px; border-radius: 999px; border: 1px solid rgba(240, 217, 163, 0.55);
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3; cursor: pointer;
  font: 700 16px/1.2 var(--ui); letter-spacing: 0.02em; box-shadow: 0 14px 30px -10px rgba(78, 39, 118, 0.7);
  transition: transform 120ms ease, box-shadow 160ms ease, opacity 160ms ease, background-color 200ms ease;
}
.gs-go > span { min-width: 0; overflow-wrap: anywhere; text-align: center; }
.gs-go i { flex: none; font-size: 15px; color: #f3d98f; }
.gs-go:hover:not(:disabled) { box-shadow: 0 16px 34px -10px rgba(78, 39, 118, 0.85); }
.gs-go:active:not(:disabled) { transform: translateY(1px); }
.gs-go:disabled { opacity: 0.6; cursor: default; }
/* Done: the gold seal on cream ("Sent ✓", "Copied ✓"). */
.gs-go.done, .gs-go.done:hover:not(:disabled) { background: #fff6e2; border-color: var(--gold-lt); color: #5a3f0f; box-shadow: none; }
.gs-go.done:disabled { opacity: 1; }
.gs-go-seal i { color: #4a3209; font-size: 12px; }
.gs-go-seal { flex: none; width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; color: #4a3209; font: 700 13px var(--ui);
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a 100%); box-shadow: inset 0 0 0 2px rgba(255, 240, 200, 0.35); }
.gs-go.done .gs-go-seal { animation: gs-press 420ms cubic-bezier(.2, .9, .3, 1.25) both; }
.gs-go-breathe { width: 24px; height: 24px; font-size: 11px; animation: gs-breathe 1.2s ease-in-out infinite; }
@keyframes gs-breathe { 50% { transform: scale(0.86); opacity: 0.8; } }
@keyframes gs-press { 0% { transform: scale(1.3) rotate(-20deg); opacity: 0; } 100% { transform: none; opacity: 1; } }
.gs-copied { margin: 12px 2px 0; text-align: center; font: 500 14px/1.45 var(--ui); color: var(--ink2); text-wrap: balance; }

/* The secondary and the lock pill, on one row. */
.gs-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 14px 0 0; min-width: 0; flex-wrap: wrap; }
.gs-row.solo { justify-content: center; }
.gs-second {
  flex: none; display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 18px; border-radius: 999px;
  border: 1.5px solid #ddd0ef; background: #fff; cursor: pointer; font: 700 14.5px var(--ui); color: var(--amethyst);
}
.gs-second:hover:not(:disabled) { border-color: #b9a2dc; }
.gs-second:disabled { opacity: 0.6; cursor: default; }
.gs-second.done { background: #fff6e2; border-color: var(--gold-lt); color: #5a3f0f; }
.gs-lock { display: inline-flex; align-items: center; gap: 8px; margin: 0; min-width: 0; font: 500 13.5px/1.4 var(--ui); color: var(--quiet); }
.gs-lock i { flex: none; color: #c9a94e; font-size: 12px; }
.gs-fallback .gs-lock { align-self: center; margin-top: 14px; }

.gs-err { margin: 10px 0 0; text-align: center; font: 600 13px/1.45 var(--ui); color: #a5531f; }
.gs-blocked { margin: 22px 0 0; padding: 12px 14px; border-radius: 14px; background: #f6efe1; border: 1px solid var(--paper-edge);
  color: #5a2f87; font: 500 14px/1.45 var(--ui); text-align: center; }
/* Short of carats: the line, then the gold way on. */
.gs-short { margin: 22px 0 0; text-align: center; font: 600 15px/1.45 var(--ui); color: #5a2f87; }
.gs-short + .gs-go { margin-top: 12px; }
.gs-go.gs-go-gold { background: linear-gradient(180deg, #f3d98f, #c9a94e); border-color: #c9a94e; color: #2a1845;
  box-shadow: 0 14px 30px -12px rgba(138, 106, 42, 0.75); }
.gs-go.gs-go-gold:hover { box-shadow: 0 16px 34px -12px rgba(138, 106, 42, 0.9); }

/* Both failed */
.gs-fallback { display: flex; flex-direction: column; }
.gs-fb-panel { margin: 14px 0 0; padding: 14px; border-radius: 14px; background: #fff; border: 1px solid var(--paper-edge); }
.gs-fb-say { margin: 0 0 8px; font: 600 13.5px/1.4 var(--ui); color: var(--ink); }
.gs-fb-url:focus, .gs-fb-msg:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 3px rgba(123, 75, 176, 0.15); }
.gs-fb-url { display: block; width: 100%; box-sizing: border-box; min-height: 44px; padding: 10px 12px; border-radius: 10px;
  border: 1.5px solid #d9c9ef; background: #fff; color: var(--ink); font: 500 14px ui-monospace, SFMono-Regular, Menlo, monospace; text-overflow: ellipsis; }
.gs-fb-label { margin: 12px 0 6px; font: 600 12.5px var(--ui); color: var(--ink2); }
.gs-fb-msg { display: block; width: 100%; box-sizing: border-box; padding: 10px 12px; border-radius: 10px; border: 1.5px solid #e3d4f5;
  background: #fff; color: var(--ink2); font: 500 15px/1.4 var(--serif); resize: none; }
.gs-fb-copy { margin-top: 8px; min-height: 44px; padding: 8px 16px; border-radius: 999px; border: 1px solid #cdb8ea; background: #fff; cursor: pointer;
  color: var(--amethyst); font: 600 13px var(--ui); display: inline-flex; align-items: center; gap: 8px; }

/* The preview sheet over the send screen (a phone). */
.gs-sheet { padding-top: 16px; }

.gs-x:focus-visible, .gs-name-edit:focus-visible, .gs-sign:focus-visible, .gs-go:focus-visible, .gs-second:focus-visible,
.gs-fb-copy:focus-visible, .gs-peek:focus-visible, .gs-sign-other:focus-visible {
  outline: 2px solid var(--violet); outline-offset: 3px; }

@media (prefers-reduced-motion: reduce) {
  .gs-go-breathe, .gs-go.done .gs-go-seal { animation: none; }
  .gs-go { transition: none; }
}

/* A phone: a sheet from the bottom, as tall as what it holds. */
@media (max-width: 560px) {
  .gsend-overlay { align-items: flex-end; padding: 0; }
  .gsend {
    max-width: none; max-height: calc(100dvh - 24px); border-radius: 26px 26px 0 0;
    padding: 14px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  }
  .gs-grab { display: block; flex: none; width: 40px; height: 5px; border-radius: 3px; background: #d9cbb0; margin: 0 auto 12px; }
  .gs-name { font-size: 23px; }
}
@media (max-width: 380px) {
  .gsend { padding-left: 16px; padding-right: 16px; }
  .gs-name { font-size: 21px; }
  .gs-pic + .gs-paper { margin-left: 6px; margin-right: 6px; }
  .gs-paper { padding: 24px 12px 12px; }
  .gs-peek { margin-left: 6px; margin-right: 6px; gap: 12px; }
  .gs-fan { width: 96px; --step: 17px; }
  .gs-note { font-size: 18px; }
  .gs-second { padding: 0 14px; }
}
/* The send step (mystic-page 2026-10-29, PNGs 25/26): "on us" for someone
   new, and the Circle ask, under To. */
.gs-onus { margin: 12px 2px 0; }
.gs-onus-main { display: flex; gap: 8px; margin: 0; font-family: var(--ui); font-size: 15px; font-weight: 700; line-height: 1.35; color: var(--ink); }
.gs-onus-star { color: #c9a94e; flex: 0 0 auto; }
.gs-onus-sub { margin: 4px 0 0 22px; font-family: var(--ui); font-size: 13px; line-height: 1.4; color: rgba(60, 40, 90, 0.72); }
.gs-ask { position: relative; display: flex; align-items: center; gap: 10px; margin: 12px 2px 0; min-height: 44px; cursor: pointer; font-family: var(--ui); font-size: 15px; font-weight: 700; color: var(--ink); }
/* The real checkbox covers its whole line, so the line is the touch target
   (e2e round 2: the 22px box alone was the target); the box is drawn beside
   the words as it was. */
.gs-ask-box { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: pointer; z-index: 1; }
.gs-ask-mark { flex: 0 0 auto; width: 22px; height: 22px; box-sizing: border-box; border-radius: 5px; border: 1.5px solid #8a7a9f;
  background: #fff; display: grid; place-items: center; transition: background-color 120ms ease, border-color 120ms ease; }
.gs-ask-box:checked + .gs-ask-mark { background: #6b3fa0; border-color: #6b3fa0; }
.gs-ask-box:checked + .gs-ask-mark::after { content: ''; width: 6px; height: 11px; margin-top: -2px; border: solid #fff; border-width: 0 2.5px 2.5px 0; transform: rotate(45deg); }
.gs-ask-box:focus-visible + .gs-ask-mark { outline: 2px solid #6b3fa0; outline-offset: 2px; }
</style>
